import { z } from "zod";

/**
 * The organization data export's public vocabulary: which data sets exist, what
 * a request may ask for, and what a finished export lists.
 *
 * Browser-safe on purpose (zod only, no database, no storage): the settings UI
 * renders its data-set checkboxes from `EXPORT_DATA_SETS` and validates its
 * request with the same schema the API route does, so the two can never drift.
 * The server-side builders (`listExportDataSets`) use exactly these names, in
 * this order, and a test pins that.
 */

/** One selectable data set, as the request form and the README describe it. */
export interface ExportDataSetInfo {
	/** File base name, e.g. "prompt-answers"; files are `<name>-<n>.csv.gz`. */
	name: string;
	label: string;
	description: string;
}

/** Every CSV data set, in the order the export writes them. */
export const EXPORT_DATA_SETS: ReadonlyArray<ExportDataSetInfo> = [
	{
		name: "prompts",
		label: "Prompts",
		description:
			"The prompts you track, with their category, use case and tags.",
	},
	{
		name: "prompt-answers",
		label: "Prompt answers",
		description:
			"Every answer an AI assistant gave to one of your prompts, with the summary text and where your brand landed in it.",
	},
	{
		name: "brand-mentions",
		label: "Brand mentions",
		description:
			"Each brand named in an AI answer, whether it is one of your tracked competitors, and the surrounding context.",
	},
	{
		name: "comparison-verdicts",
		label: "Comparison verdicts",
		description:
			"For head-to-head comparison prompts: what each answer said each brand is good and bad at.",
	},
	{
		name: "citations",
		label: "Citations",
		description:
			"The sources AI assistants cited when answering your prompts.",
	},
	{
		name: "ai-search-queries",
		label: "AI search queries",
		description:
			"The searches assistants ran behind the scenes while answering your prompts.",
	},
	{
		name: "search-rankings",
		label: "Search rankings",
		description: "Search-result positions we recorded for those searches.",
	},
	{
		name: "placements",
		label: "Placements",
		description:
			"Earned-media placements you scored, with their quality score and sentiment.",
	},
	{
		name: "coverage",
		label: "Coverage",
		description: "Your coverage groups and the media URLs in each.",
	},
	{
		name: "pitch-lists",
		label: "Pitch lists",
		description:
			"Your pitch lists: the publishers you plan to pitch, with each one's status and note.",
	},
	{
		name: "competitors",
		label: "Competitors",
		description: "The competitors you track for each property.",
	},
	{
		name: "annotations",
		label: "Annotations",
		description: "Your timeline annotations.",
	},
	{
		name: "key-messages",
		label: "Key messages",
		description: "The messages you track for pull-through in AI answers.",
	},
	{
		name: "site-audits",
		label: "Site audits",
		description: "Completed site audits and their scores.",
	},
	{
		name: "site-audit-pages",
		label: "Site audit pages",
		description: "Per-page results within those audits.",
	},
	{
		name: "faqs",
		label: "FAQs",
		description: "FAQs generated for your pages.",
	},
];

const EXPORT_DATA_SET_NAMES: ReadonlySet<string> = new Set(
	EXPORT_DATA_SETS.map((dataSet) => dataSet.name),
);

/** Whether a string names a data set in the catalog. */
export function isExportDataSetName(name: string): boolean {
	return EXPORT_DATA_SET_NAMES.has(name);
}

/** A calendar day, `YYYY-MM-DD`, that is a real date. */
const isoDay = z
	.string()
	.regex(/^\d{4}-\d{2}-\d{2}$/, "Use the YYYY-MM-DD format")
	.refine(
		(value) => {
			const date = new Date(`${value}T00:00:00.000Z`);
			return (
				!Number.isNaN(date.getTime()) &&
				date.toISOString().slice(0, 10) === value
			);
		},
		{ message: "Not a valid date" },
	);

/**
 * What a data export request may narrow itself to. Every field is optional,
 * and an omitted field means "everything": `{}` is the full export.
 *
 * - `propertyIds`: properties of the requesting organization. A brand's
 *   LOCATION children come along with it.
 * - `dataSets`: names from `EXPORT_DATA_SETS`.
 * - `from` / `to`: calendar days in UTC, both inclusive. See the README of an
 *   export for which date each data set is filtered on.
 */
export const dataExportScopeSchema = z
	.object({
		propertyIds: z.array(z.string().min(1)).min(1).max(1000).optional(),
		dataSets: z
			.array(z.string())
			.min(1)
			.optional()
			.refine((names) => !names || names.every(isExportDataSetName), {
				message: "Unknown data set",
			}),
		from: isoDay.optional(),
		to: isoDay.optional(),
	})
	.refine((scope) => !scope.from || !scope.to || scope.from <= scope.to, {
		message: "`from` must not be after `to`",
		path: ["from"],
	});

export type DataExportScope = z.infer<typeof dataExportScopeSchema>;

/**
 * One delivered file of a finished export, as recorded on the export row.
 * `key` is the storage object key and never leaves the server; the list route
 * strips it. `dataSet` is null for the README, the manifest and the brand
 * profile.
 */
export interface DataExportFile {
	name: string;
	key: string;
	bytes: number;
	rows: number;
	dataSet: string | null;
}

/** A delivered file as the list route returns it: everything but the key. */
export type DataExportFileSummary = Omit<DataExportFile, "key">;
